// File: frontend/src/components/messages/ConversationView.tsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { MdKeyboard, MdInsertEmoticon } from "react-icons/md";
import { RiDeleteBin6Line } from "react-icons/ri";
import { useAuth } from "../../context/AuthContext";
import { MessageDto } from "../../models/MessageModel";
import {
  getMessages,
  markConversationAsRead,
  sendMessageRest,
  deleteMessage,
} from "../../services/messageService";
import {
  sendMessageWs,
  markAsReadWs,
  onMessageEvent,
  onWebSocketError,
  dispatchMessageEvent,
} from "../../services/websocketService";
import { generateProfileUrl } from "../../utils/slugGenerator";
import { parseServerDate } from "../../utils/dateUtils";
import { MessageBubble } from "./MessageBubble";
import { MessageReportModal } from "./MessageReportModal";
import { getReportedMessageIds } from "../../services/messageReportService";
import { EntryEditorToolbar } from "../entries/EntryEditorToolbar";
// Ensure toolbar CSS is loaded even when no entry components are mounted
import "../entries/EntryPostForm.css";
import "../entries/RefSearchPopup.css";
import { MESSAGES_PER_PAGE } from "../../constants/settings.constants";
import {
  EMOJI_GROUPS,
  EMOJI_SHORTCUTS_BY_EMOJI,
  replaceEmojiShortcutAt,
} from "../../constants/emoji.constants";
import { useSiteSettings } from "../../context/SiteSettingsContext";
import { translateMessageErrorCode } from "../../utils/parseApiError";

// Turkish characters for the keyboard popup
const TURKISH_CHARS = ["ç", "ğ", "ı", "ö", "ş", "ü", "Ç", "Ğ", "İ", "Ö", "Ş", "Ü"];

interface ConversationViewProps {
  conversationId: number | null;
  otherUserId: number;
  otherUsername: string;
  otherUserRole: string;
  onBack: () => void;
  /** Called when a new conversation is created by sending the first message */
  onConversationCreated?: (conversationId: number) => void;
  /**
   * Optional text the composer should start with (e.g. the entry reference added
   * when the conversation was opened from an entry's "send message" menu item).
   */
  initialDraft?: string;
  /** Called right after initialDraft has been copied into the composer */
  onDraftConsumed?: () => void;
}

/**
 * Whether two server timestamps fall on the same calendar day in the READER's
 * timezone.
 *
 * parseServerDate is what makes that true: the backend sends a bare
 * LocalDateTime, which the browser would otherwise read as local wall-clock time
 * and shift by the viewer's offset — putting a late-evening message on the wrong
 * side of midnight and producing a day separator that is a day out.
 */
function isSameLocalDay(a: string, b: string): boolean {
  const first = parseServerDate(a);
  const second = parseServerDate(b);
  return (
    first.getFullYear() === second.getFullYear() &&
    first.getMonth() === second.getMonth() &&
    first.getDate() === second.getDate()
  );
}

/**
 * Label for a day separator: "Bugün", "Dün", or the full date for anything older.
 *
 * The locale follows the interface language rather than being pinned to Turkish,
 * so an English reader does not get "12 Eylül 2026" in the middle of an English UI.
 */
function formatDaySeparatorLabel(
  dateStr: string,
  t: (key: string) => string,
  language: string
): string {
  const date = parseServerDate(dateStr);
  const now = new Date();

  if (isSameLocalDay(dateStr, now.toISOString())) {
    return t("messages_today");
  }

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (isSameLocalDay(dateStr, yesterday.toISOString())) {
    return t("messages_yesterday");
  }

  return date.toLocaleDateString(language.startsWith("en") ? "en-GB" : "tr-TR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

// Maximum characters of the quoted message shown in the composer's quote bar.
// Only a hint is needed there — the full text stays one click away in the thread.
const COMPOSER_QUOTE_PREVIEW_MAX_LENGTH = 120;

/**
 * Flatten the quoted message into the single line shown in the composer quote bar.
 * Newlines collapse so a long original cannot push the composer around.
 */
function buildComposerQuotePreview(content: string): string {
  const flattened = content.replace(/\s+/g, " ").trim();
  return flattened.length > COMPOSER_QUOTE_PREVIEW_MAX_LENGTH
    ? flattened.slice(0, COMPOSER_QUOTE_PREVIEW_MAX_LENGTH) + "…"
    : flattened;
}

// ─── Delete message confirmation modal ───────────────────────────────────────

interface DeleteMessageModalProps {
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Confirmation modal for deleting a single message.
 * Explains that the deletion is only on the current user's side.
 */
const DeleteMessageModal: React.FC<DeleteMessageModalProps> = ({ onConfirm, onCancel }) => {
  const { t } = useTranslation();
  return (
    <div className="msg-confirm-overlay" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="msg-confirm-modal">
        <h4 className="msg-confirm-title">{t("messages_delete_message_title")}</h4>
        <p className="msg-confirm-question">{t("messages_delete_message_confirm")}</p>
        <p className="msg-confirm-note">ℹ️ {t("messages_delete_message_note")}</p>
        <div className="msg-confirm-actions">
          <button className="msg-confirm-cancel" onClick={onCancel}>
            {t("admin_cancel")}
          </button>
          <button className="msg-confirm-delete" onClick={onConfirm}>
            {t("admin_action_delete")}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── ConversationView ─────────────────────────────────────────────────────────

/**
 * Displays the message thread for a selected conversation.
 * Supports sending messages via WebSocket (with REST fallback),
 * infinite scroll for older messages, and real-time incoming messages.
 *
 * When conversationId is null, this is a new conversation — the first
 * message sent via REST will create the conversation on the backend.
 */
export const ConversationView: React.FC<ConversationViewProps> = ({
  conversationId,
  otherUserId,
  otherUsername,
  otherUserRole,
  onBack,
  onConversationCreated,
  initialDraft,
  onDraftConsumed,
}) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  /*
   * The composer's limit is the admin-configured one, not a compiled-in number.
   * It used to be a constant, which meant raising messaging.max_length in the
   * panel changed what the backend accepted but not what the textarea allowed —
   * the setting looked like it worked and did nothing for the writer.
   */
  const { messageMaxLength } = useSiteSettings();
  const navigate = useNavigate();

  const [messages, setMessages] = useState<MessageDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [inputValue, setInputValue] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showTrKeyboard, setShowTrKeyboard] = useState(false);
  // Emoji popup visibility and the group (tab) it currently shows
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [activeEmojiGroupId, setActiveEmojiGroupId] = useState<string>(EMOJI_GROUPS[0].id);
  // Emoji under the pointer in the popup — its shortcuts are shown in the popup footer
  const [hoveredEmoji, setHoveredEmoji] = useState<string | null>(null);
  // Track the real conversationId (may be set after first message for new conversations)
  const [activeConversationId, setActiveConversationId] = useState<number | null>(conversationId);
  // Message pending deletion — null means no modal open
  const [pendingDeleteMessageId, setPendingDeleteMessageId] = useState<number | null>(null);
  // Message pending report — null means no modal open
  const [pendingReportMessageId, setPendingReportMessageId] = useState<number | null>(null);
  // Set of message IDs that have been reported in this session
  const [reportedMessageIds, setReportedMessageIds] = useState<Set<number>>(new Set());
  // Message the composer is currently quoting — null means a normal (non-reply) send
  const [replyTo, setReplyTo] = useState<MessageDto | null>(null);
  // Message briefly highlighted after jumping to it from a quote block
  const [highlightedMessageId, setHighlightedMessageId] = useState<number | null>(null);
  /*
   * ID of the oldest message that was still unread when this conversation was
   * opened — the divider is drawn just above it.
   *
   * Captured once, on the first page load, and deliberately never updated
   * afterwards: messages that arrive while the reader is sitting in the thread
   * are being watched as they come in, so a second "unread from here" line would
   * be noise. Leaving and re-entering clears it, because by then everything has
   * been marked read.
   */
  const [unreadDividerId, setUnreadDividerId] = useState<number | null>(null);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Wraps the emoji button and its popup — clicks inside it do not close the popup
  const emojiWrapperRef = useRef<HTMLDivElement>(null);
  /*
   * Mirror of `messages` kept in a ref.
   *
   * handleQuoteClick walks backwards through older pages until it finds the quoted
   * message, and needs to test the CURRENT list on every iteration. Reading the
   * `messages` state inside that loop would keep seeing the snapshot captured when
   * the handler was created, so the ref is what the loop reads instead.
   */
  const messagesRef = useRef<MessageDto[]>([]);

  // Keep the ref in step with the rendered list
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Sync activeConversationId when prop changes
  useEffect(() => {
    setActiveConversationId(conversationId);
  }, [conversationId]);

  // Load messages for this conversation
  const loadMessages = useCallback(
    async (pageNum: number) => {
      if (!activeConversationId) return; // New conversation — no messages to load
      try {
        setLoading(true);
        const response = await getMessages(activeConversationId, pageNum, MESSAGES_PER_PAGE);
        if (pageNum === 0) {
          /*
           * Find the oldest message the reader had not seen yet. content arrives
           * newest-first, so it is reversed to walk forwards in time and the first
           * hit is the oldest unread one. Own messages are skipped — the sender
           * has obviously read those.
           *
           * This must happen on the response of THIS request: the conversation is
           * marked as read right after it, and from then on every message comes
           * back with read: true.
           */
          const firstUnread = [...response.content]
            .reverse()
            .find((m) => !m.read && m.senderId !== user?.id);
          setUnreadDividerId(firstUnread ? firstUnread.id : null);

          setMessages(response.content);
        } else {
          // Append older messages (content comes in DESC order from backend)
          setMessages((prev) => [...prev, ...response.content]);
        }
        setTotalPages(response.totalPages);
        setPage(pageNum);
      } catch (err) {
        console.error("Failed to load messages:", err);
      } finally {
        setLoading(false);
      }
    },
    [activeConversationId, user?.id]
  );

  // Load first page and mark as read when conversation changes
  useEffect(() => {
    setMessages([]);
    setPage(0);
    setInputValue("");
    setError(null);
    setShowTrKeyboard(false);
    setShowEmojiPicker(false);
    setPendingDeleteMessageId(null);
    setPendingReportMessageId(null);
    setReportedMessageIds(new Set());
    setReplyTo(null);
    setHighlightedMessageId(null);
    setUnreadDividerId(null);

    if (!activeConversationId) return;

    /*
     * Load first, mark as read second — the order matters.
     *
     * Both used to be fired together, which raced: when the read call won, the
     * message list came back with everything already read and the unread divider
     * could never be computed. Awaiting the load closes that window.
     *
     * cancelled guards against the reader switching conversations mid-flight, so
     * a slow response cannot mark the wrong thread as read.
     */
    let cancelled = false;
    (async () => {
      await loadMessages(0);
      if (cancelled) return;

      markAsReadAndUpdateBadge(activeConversationId);

      /*
       * Restore which messages this user has already reported.
       *
       * The flags used to live only in React state, so a refresh or a switch to
       * another conversation wiped them: the user would report the same message
       * again, fill in the whole form, and only then be told it was a duplicate.
       *
       * One request covers the entire conversation, including pages that have not
       * been fetched yet, so "load older messages" needs no follow-up call.
       *
       * A failure here is not worth an error banner — the thread itself loaded
       * fine, and the only cost is a report button reappearing on a message that
       * was already reported. The backend still refuses the duplicate.
       */
      try {
        const reportedIds = await getReportedMessageIds(activeConversationId);
        if (!cancelled) {
          setReportedMessageIds(new Set(reportedIds));
        }
      } catch (err) {
        console.error("Failed to load reported message IDs:", err);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConversationId, loadMessages]);

  /*
   * Copy the caller-supplied draft into the composer.
   *
   * Declared after the reset effect on purpose: both run in the same commit on
   * mount and effects fire in declaration order, so the draft is applied after
   * the reset clears the input instead of being wiped by it. The parent clears
   * the draft through onDraftConsumed, which makes this a one-shot operation —
   * anything the user types afterwards is never overwritten.
   */
  useEffect(() => {
    if (!initialDraft) return;

    setInputValue(initialDraft);
    onDraftConsumed?.();

    // Put the caret at the end so the user can keep typing straight away
    requestAnimationFrame(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      textarea.focus();
      textarea.selectionStart = initialDraft.length;
      textarea.selectionEnd = initialDraft.length;
      textarea.style.height = "auto";
      textarea.style.height = Math.min(textarea.scrollHeight, 120) + "px";
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDraft]);

  /**
   * Mark a conversation as read via both WebSocket and REST.
   *
   * WebSocket path (markAsReadWs) is the primary route — the backend sends
   * an UNREAD_COUNT event back through the WebSocket, which Header.tsx listens
   * to and updates the badge.
   *
   * REST path (markConversationAsRead) is the fallback. Unlike the WS path,
   * the REST response is NOT automatically wired to Header.tsx. After the REST
   * call succeeds we manually dispatch a synthetic UNREAD_COUNT event via
   * dispatchMessageEvent so the badge always clears, even when WebSocket is
   * unavailable or not yet connected at the time of opening a conversation.
   */
  const markAsReadAndUpdateBadge = (convId: number): void => {
    // WebSocket path: server will push UNREAD_COUNT back to the client
    markAsReadWs(convId);

    // REST path: use the returned totalUnread to update the badge manually
    markConversationAsRead(convId)
      .then(({ totalUnread }) => {
        dispatchMessageEvent({
          type: "UNREAD_COUNT",
          message: null,
          conversationId: null,
          totalUnread,
        });
      })
      .catch(() => {
        // Ignore REST failures silently — WS path may have already succeeded
      });
  };

  // Listen for WebSocket errors (e.g. privacy-blocked) and show them in the UI.
  // Without this, errors from the WS path (/user/queue/errors) are silently dropped
  // because the REST catch block is never reached when WebSocket is connected.
  useEffect(() => {
    const unsubscribeWsError = onWebSocketError((rawCode: string) => {
      // translateMessageErrorCode returns null when the code has no translation
      setError(translateMessageErrorCode(rawCode, t) ?? t("messages_send_error"));
      setSending(false);
    });
    return unsubscribeWsError;
  }, [t]);

  // Listen for real-time incoming messages in THIS conversation
  useEffect(() => {
    const unsubscribe = onMessageEvent((event) => {

      /*
       * MESSAGES_READ — the recipient has read this conversation.
       * Flip all visible messages to read: true so the sender sees ✓✓
       * in real time without needing to refresh the page.
       */
      if (event.type === "MESSAGES_READ" && event.conversationId === activeConversationId) {
        setMessages((prev) =>
          prev.map((m) => (m.read ? m : { ...m, read: true }))
        );
      }

      if (event.type === "NEW_MESSAGE" && event.message) {
        const matchesConversation =
          (activeConversationId && event.message.conversationId === activeConversationId) ||
          (!activeConversationId && event.message.senderId === user?.id);

        if (matchesConversation) {
          // Update activeConversationId if this is a new conversation
          if (!activeConversationId && event.message.conversationId) {
            setActiveConversationId(event.message.conversationId);
            onConversationCreated?.(event.message.conversationId);
          }

          // Add new message at the beginning (DESC order, newest first)
          setMessages((prev) => {
            if (prev.some((m) => m.id === event.message!.id)) {
              return prev;
            }
            return [event.message!, ...prev];
          });

          // Mark as read immediately since user is viewing this conversation
          if (event.message.senderId !== user?.id && event.message.conversationId) {
            markAsReadAndUpdateBadge(event.message.conversationId);
          }
        }
      }
    });

    return unsubscribe;
  }, [activeConversationId, user?.id, onConversationCreated]);

  /*
   * Close the emoji popup on any press outside of it (the textarea included).
   *
   * mousedown / touchstart are used instead of click so the popup is already gone
   * when the press lands, e.g. on the send button. The listener only exists while
   * the popup is open.
   */
  useEffect(() => {
    if (!showEmojiPicker) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const wrapper = emojiWrapperRef.current;
      if (wrapper && !wrapper.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [showEmojiPicker]);

  // Auto-resize textarea
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textarea = e.target;
    let value = textarea.value;

    /*
     * Emoji shortcuts (":D" -> 😄): when the character just typed is a space or a
     * new line, the token right before it is checked against EMOJI_SHORTCUTS.
     *
     * Only real typing triggers it (inputType insertText / insertLineBreak) — a
     * pasted text keeps its ":D" exactly as it was pasted.
     */
    const inputType = (e.nativeEvent as InputEvent).inputType;
    const caret = textarea.selectionStart;
    if (
      (inputType === "insertText" || inputType === "insertLineBreak") &&
      caret > 0 &&
      /\s/.test(value[caret - 1])
    ) {
      const replaced = replaceEmojiShortcutAt(value, caret - 1);
      if (replaced) {
        value = replaced.text;
        const newCaret = caret + replaced.delta;
        // Setting the state moves the caret to the end — put it back after the space
        requestAnimationFrame(() => {
          textarea.selectionStart = newCaret;
          textarea.selectionEnd = newCaret;
        });
      }
    }

    setInputValue(value);
    setError(null);
    textarea.style.height = "auto";
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + "px";
  };

  /*
   * Insert text at the caret (replacing any selection) and put the caret right
   * after it. Shared by the Turkish keyboard and the emoji popup.
   *
   * char.length is used for the new caret position on purpose: most emoji are
   * two UTF-16 code units (some, like "❤️", even more), and selectionStart /
   * selectionEnd are measured in code units too — so the caret never lands in
   * the middle of an emoji.
   */
  const insertAtCursor = (text: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newContent = inputValue.slice(0, start) + text + inputValue.slice(end);
    setInputValue(newContent);
    setError(null);
    requestAnimationFrame(() => {
      const pos = start + text.length;
      textarea.selectionStart = pos;
      textarea.selectionEnd = pos;
      textarea.focus();
    });
  };

  // Insert a Turkish character at cursor position
  const insertTurkishChar = (char: string) => {
    insertAtCursor(char);
  };

  // Insert an emoji at cursor position — the popup stays open so several can be added in a row
  const insertEmoji = (emoji: string) => {
    insertAtCursor(emoji);
  };

  // The two popups share the same spot above the action row, so opening one closes the other
  const toggleTrKeyboard = () => {
    setShowEmojiPicker(false);
    setShowTrKeyboard((prev) => !prev);
  };

  const toggleEmojiPicker = () => {
    setShowTrKeyboard(false);
    setHoveredEmoji(null);
    setShowEmojiPicker((prev) => !prev);
  };

  const activeEmojiGroup =
    EMOJI_GROUPS.find((group) => group.id === activeEmojiGroupId) ?? EMOJI_GROUPS[0];

  // Shortcuts of the hovered emoji; null when nothing is hovered or it has none
  const hoveredEmojiShortcuts = hoveredEmoji ? EMOJI_SHORTCUTS_BY_EMOJI[hoveredEmoji] ?? null : null;

  // Send message
  const handleSend = async () => {
    const trimmed = inputValue.trim();
    /*
     * A shortcut typed as the very last token has no space after it yet, so it is
     * converted here — otherwise ":D" + Enter would go out as plain text.
     */
    const content = replaceEmojiShortcutAt(trimmed, trimmed.length)?.text ?? trimmed;
    if (!content || sending) return;

    if (content.length > messageMaxLength) {
      setError(t("messages_content_too_long"));
      return;
    }

    setSending(true);
    setError(null);

    /*
     * The quote target is captured before the await so a reply that is armed and
     * sent in the same tick cannot be lost by the reset below. A brand new
     * conversation has nothing to quote yet, so the id is only sent when this
     * conversation already exists on the backend.
     */
    const replyToMessageId = activeConversationId && replyTo ? replyTo.id : null;

    try {
      if (activeConversationId) {
        const sentViaWs = sendMessageWs(otherUserId, content, replyToMessageId);
        if (!sentViaWs) {
          const newMessage = await sendMessageRest({
            recipientId: otherUserId,
            content,
            replyToMessageId,
          });
          setMessages((prev) => {
            if (prev.some((m) => m.id === newMessage.id)) return prev;
            return [newMessage, ...prev];
          });
        }
      } else {
        const newMessage = await sendMessageRest({ recipientId: otherUserId, content });
        setActiveConversationId(newMessage.conversationId);
        onConversationCreated?.(newMessage.conversationId);
        setMessages([newMessage]);
      }

      setInputValue("");
      setReplyTo(null);
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }
    } catch (err: any) {
      /*
       * Translate backend error codes using i18n keys.
       * Backend sends the raw error code string (e.g. "MESSAGE_SENDER_NOT_ACTIVE")
       * as err.message. Try to translate it; fall back to the generic send error.
       */
      const rawCode: string = err?.message || "";
      setError(translateMessageErrorCode(rawCode, t) ?? t("messages_send_error"));
    } finally {
      setSending(false);
    }
  };

  // Handle Enter key to send (Shift+Enter for new line)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Escape closes an open emoji popup without touching the draft
    if (e.key === "Escape" && showEmojiPicker) {
      e.preventDefault();
      setShowEmojiPicker(false);
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      setShowEmojiPicker(false);
      handleSend();
    }
  };

  /**
   * Arm the composer with a quote target and move the caret into the textarea,
   * so the user can start typing the reply without a second click.
   */
  const handleReplyRequest = (message: MessageDto) => {
    setReplyTo(message);
    requestAnimationFrame(() => textareaRef.current?.focus());
  };

  /**
   * Scroll to a message and flash it, returning false when it is not on screen.
   * The anchor id is written by MessageBubble on the bubble wrapper.
   */
  const scrollToMessage = (messageId: number): boolean => {
    const element = document.getElementById(`message-${messageId}`);
    if (!element) return false;

    element.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightedMessageId(messageId);
    // Clear the flash, but only if no other jump has started in the meantime
    window.setTimeout(() => {
      setHighlightedMessageId((current) => (current === messageId ? null : current));
    }, 2000);
    return true;
  };

  /**
   * Jump to the message a quote block points at.
   *
   * The thread is paginated newest-first, so a quoted message from earlier in the
   * conversation may simply not be loaded yet. In that case older pages are pulled
   * in one by one until the target shows up — the same pages the "load older"
   * button would fetch, just without making the user click it repeatedly.
   */
  const handleQuoteClick = async (messageId: number) => {
    if (messagesRef.current.some((m) => m.id === messageId)) {
      requestAnimationFrame(() => scrollToMessage(messageId));
      return;
    }

    if (!activeConversationId || loading) return;

    setLoading(true);
    try {
      let nextPage = page + 1;
      let knownTotalPages = totalPages;
      let found = false;

      while (nextPage < knownTotalPages && !found) {
        const response = await getMessages(activeConversationId, nextPage, MESSAGES_PER_PAGE);

        setMessages((prev) => {
          const seen = new Set(prev.map((m) => m.id));
          const merged = [...prev, ...response.content.filter((m) => !seen.has(m.id))];
          // Keep the ref in step immediately — the loop below reads it on the next pass
          messagesRef.current = merged;
          return merged;
        });

        knownTotalPages = response.totalPages;
        setTotalPages(response.totalPages);
        setPage(nextPage);

        found = response.content.some((m) => m.id === messageId);
        nextPage += 1;
      }

      if (found) {
        // Wait for the newly appended bubbles to be committed before measuring
        requestAnimationFrame(() => {
          window.setTimeout(() => scrollToMessage(messageId), 50);
        });
      } else {
        // The original was deleted on this side, or lies before a conversation clear
        setError(t("messages_reply_original_missing"));
      }
    } catch (err) {
      console.error("Failed to load the quoted message:", err);
      setError(t("messages_reply_original_missing"));
    } finally {
      setLoading(false);
    }
  };

  // Open the delete confirmation modal — does NOT delete immediately
  const handleDeleteRequest = (messageId: number) => {
    setPendingDeleteMessageId(messageId);
  };

  // Open the message report modal
  const handleReportRequest = (messageId: number) => {
    setPendingReportMessageId(messageId);
  };

  // Mark message as reported in local state after successful submission
  const handleReportSuccess = (messageId: number) => {
    setReportedMessageIds((prev) => new Set(prev).add(messageId));
  };

  // Execute the deletion after user confirms in the modal
  const handleDeleteConfirm = async () => {
    if (pendingDeleteMessageId === null) return;
    try {
      await deleteMessage(pendingDeleteMessageId);
      setMessages((prev) => prev.filter((m) => m.id !== pendingDeleteMessageId));
      // Deleting the message the composer is quoting would leave a quote of nothing
      setReplyTo((current) => (current?.id === pendingDeleteMessageId ? null : current));
    } catch (err) {
      console.error("Failed to delete message:", err);
    } finally {
      setPendingDeleteMessageId(null);
    }
  };

  // Load older messages
  const handleLoadMore = () => {
    if (page + 1 < totalPages && !loading) {
      loadMessages(page + 1);
    }
  };

  // Navigate to user profile
  const handleUsernameClick = () => {
    const profileUrl = generateProfileUrl(otherUsername, otherUserId);
    navigate(profileUrl);
  };

  const charCount = inputValue.length;
  const isOverLimit = charCount > messageMaxLength;
  const isNearLimit = charCount > messageMaxLength * 0.9;

  return (
    <>
      {/* Chat header */}
      <div className="chat-header">
        <button className="chat-back-btn" onClick={onBack}>
          ←
        </button>
        <span className="chat-header-username" onClick={handleUsernameClick}>
          {otherUsername}
        </span>
        <span className="chat-header-role">
          {t(`status_${otherUserRole.toLowerCase()}`)}
        </span>
      </div>

      {/* Messages area */}
      <div className="chat-messages">
        {messages.map((msg, index) => {
          /*
           * The list is newest-first and .chat-messages is column-reverse, so a
           * later DOM sibling is drawn HIGHER on screen. That is why the
           * separators are emitted AFTER their message here: on screen they end
           * up above it, which is where a reader expects them.
           *
           * messages[index + 1] is the next OLDER message. A day separator is
           * drawn whenever the day changes across that boundary — and for the
           * very last message too, but only once every page is loaded: while
           * older pages are still unfetched the top of the list is not really
           * the start of that day.
           */
          const older = messages[index + 1];
          const showDaySeparator = older
            ? !isSameLocalDay(msg.createdAt, older.createdAt)
            : page + 1 >= totalPages;

          return (
            <React.Fragment key={msg.id}>
              <MessageBubble
                message={msg}
                isOwn={msg.senderId === user?.id}
                onDeleteRequest={handleDeleteRequest}
                onReportRequest={handleReportRequest}
                alreadyReported={reportedMessageIds.has(msg.id)}
                onReplyRequest={handleReplyRequest}
                onQuoteClick={handleQuoteClick}
                currentUserId={user?.id ?? null}
                /*
                 * Flashes for a couple of seconds after the reader jumps here from a
                 * quote block. The flag goes to the bubble itself rather than to a
                 * wrapper element: a wrapper would stretch across the whole row and
                 * draw the flash around empty space beside the balloon.
                 */
                isHighlighted={highlightedMessageId === msg.id}
              />

              {unreadDividerId === msg.id && (
                <div className="chat-unread-divider">
                  <span>{t("messages_unread_divider")}</span>
                </div>
              )}

              {showDaySeparator && (
                <div className="chat-day-separator">
                  <span>{formatDaySeparatorLabel(msg.createdAt, t, i18n.language)}</span>
                </div>
              )}
            </React.Fragment>
          );
        })}

        {activeConversationId && page + 1 < totalPages && (
          <div className="chat-load-more">
            <button onClick={handleLoadMore} disabled={loading}>
              {loading ? t("loading") : t("messages_load_older")}
            </button>
          </div>
        )}

        {loading && messages.length === 0 && (
          <div className="messages-empty-state">
            <p>{t("loading")}</p>
          </div>
        )}

        {!loading && messages.length === 0 && !activeConversationId && (
          <div className="messages-empty-state">
            <p>{t("messages_no_messages_yet")}</p>
          </div>
        )}
      </div>

      {/* Compose box: toolbar / textarea / actions — keyboard is absolute popup over textarea */}
      <div className="chat-compose">

        {/* Quote bar — shown only while a reply is armed; ✕ disarms it */}
        {replyTo && (
          <div className="chat-reply-preview">
            <div className="chat-reply-preview-body">
              <span className="chat-reply-preview-label">
                {t("messages_reply_to", {
                  username:
                    replyTo.senderId === user?.id
                      ? t("messages_reply_you")
                      : replyTo.senderUsername,
                })}
              </span>
              <span className="chat-reply-preview-text">
                {buildComposerQuotePreview(replyTo.content)}
              </span>
            </div>
            <button
              type="button"
              className="chat-reply-cancel"
              title={t("messages_reply_cancel")}
              onClick={() => setReplyTo(null)}
            >
              ✕
            </button>
          </div>
        )}

        {/* Top row: formatting buttons + char counter */}
        <div className="chat-toolbar">
          <EntryEditorToolbar
            textareaRef={textareaRef}
            content={inputValue}
            setContent={setInputValue}
            disabled={sending}
            showImageButton={false}
            showRefButtons={false}
            showQuoteButton={false}
            showSpoilerButton={false}
          />
          <span className={`chat-char-count ${isNearLimit ? "warning" : ""}`}>
            {charCount} / {messageMaxLength}
          </span>
        </div>

        {/* Textarea — always fully visible */}
        <textarea
          ref={textareaRef}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={t("messages_input_placeholder")}
          rows={1}
          maxLength={messageMaxLength + 100}
        />

        {/* Bottom row: keyboard toggle + clear on left, send on right */}
        <div className="chat-actions">
          {/* position:relative wrapper so popup can anchor to this button */}
          <div className="chat-keyboard-wrapper">
            <button
              type="button"
              className={`epf-keyboard-btn${showTrKeyboard ? " epf-keyboard-btn--active" : ""}`}
              title={t("turkish_char_keyboard")}
              onClick={toggleTrKeyboard}
            >
              <MdKeyboard size={16} />
            </button>

            {/* Floating popup — same style/size as entry form, opens upward over textarea */}
            {showTrKeyboard && (
              <div className="chat-tr-keyboard">
                {TURKISH_CHARS.map((char) => (
                  <button
                    key={char}
                    type="button"
                    className="epf-tr-char-btn"
                    onClick={() => insertTurkishChar(char)}
                  >
                    {char}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Emoji button — same wrapper pattern as the keyboard, popup opens upward */}
          <div className="chat-keyboard-wrapper" ref={emojiWrapperRef}>
            <button
              type="button"
              className={`epf-keyboard-btn${showEmojiPicker ? " epf-keyboard-btn--active" : ""}`}
              title={t("messages_emoji_picker")}
              aria-label={t("messages_emoji_picker")}
              aria-expanded={showEmojiPicker}
              onClick={toggleEmojiPicker}
            >
              <MdInsertEmoticon size={16} />
            </button>

            {showEmojiPicker && (
              <div className="chat-emoji-picker" role="dialog" aria-label={t("messages_emoji_picker")}>
                {/* Group tabs — each group's icon emoji, label as tooltip */}
                <div className="chat-emoji-tabs" role="tablist">
                  {EMOJI_GROUPS.map((group) => (
                    <button
                      key={group.id}
                      type="button"
                      role="tab"
                      aria-selected={group.id === activeEmojiGroup.id}
                      className={`chat-emoji-tab${group.id === activeEmojiGroup.id ? " chat-emoji-tab--active" : ""}`}
                      title={t(group.labelKey)}
                      onClick={() => setActiveEmojiGroupId(group.id)}
                    >
                      {group.icon}
                    </button>
                  ))}
                </div>

                <div
                  className="chat-emoji-grid"
                  role="tabpanel"
                  onMouseLeave={() => setHoveredEmoji(null)}
                >
                  {activeEmojiGroup.emojis.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      className="chat-emoji-btn"
                      /*
                       * preventDefault on mousedown keeps focus (and the caret) in the
                       * textarea, so on mobile the virtual keyboard does not collapse and
                       * reopen on every tap.
                       */
                      onMouseDown={(e) => e.preventDefault()}
                      onMouseEnter={() => setHoveredEmoji(emoji)}
                      onClick={() => insertEmoji(emoji)}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>

                {/*
                 * Shortcut footer — shows the hovered emoji's text shortcuts instantly
                 * (a native title tooltip needs about a second to appear). The row keeps
                 * its height when empty, so the popup does not jump while hovering.
                 */}
                <div className="chat-emoji-footer" aria-live="polite">
                  {hoveredEmoji && hoveredEmojiShortcuts && (
                    <>
                      <span className="chat-emoji-footer-emoji">{hoveredEmoji}</span>
                      <span>
                        {t("messages_emoji_shortcut", {
                          shortcuts: hoveredEmojiShortcuts.join("  "),
                        })}
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {inputValue.length > 0 && (
            <button
              type="button"
              className="epf-clear-btn"
              title={t("admin_clear_filters")}
              onClick={() => {
                setInputValue("");
                if (textareaRef.current) textareaRef.current.style.height = "auto";
              }}
            >
              <RiDeleteBin6Line size={16} />
            </button>
          )}

          <button
            className="chat-send-btn"
            onClick={handleSend}
            disabled={!inputValue.trim() || sending || isOverLimit}
          >
            {sending ? "..." : t("messages_send")}
          </button>
        </div>

      </div>

      {error && (
        <div className="new-message-error" style={{ padding: "4px 16px 8px" }}>
          {error}
        </div>
      )}

      {/* Message delete confirmation modal */}
      {pendingDeleteMessageId !== null && (
        <DeleteMessageModal
          onConfirm={handleDeleteConfirm}
          onCancel={() => setPendingDeleteMessageId(null)}
        />
      )}

      {/* Message report modal */}
      {pendingReportMessageId !== null && (
        <MessageReportModal
          messageId={pendingReportMessageId}
          onClose={() => setPendingReportMessageId(null)}
          onSuccess={() => handleReportSuccess(pendingReportMessageId)}
        />
      )}
    </>
  );
};